import React, { useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { mask as masker } from 'remask';

import { useAdminApi } from '../Admin/hooks/useAdminApi';
import { useAdminResource } from '../../../components/Admin';
import {
  PageHeader,
  DataTable,
  DataTableColumn,
  StatusBadge,
  BadgeTone,
  EmptyState,
  ErrorState,
  SkeletonTable,
} from '../../../components/Admin';
import Button from '../../../components/Atoms/Button';
import { ListContainer } from '../../_layout/AdminLayout.styles';
import { CourseInfos } from '../../../models/CourseInfosModels';
import { PaymentStatus, PaymentMethod } from '../../../models/PaymentModels';
import { convertNumberToBRL } from '../../../utils/functions';

import { StatsGrid, StatCard, StatValue, StatLabel } from './styles';
import CoursePicker from './components/CoursePicker';

const methodLabel: Record<PaymentMethod, string> = {
  credit_card: 'Cartão de crédito',
  free: 'Grátis',
  pix: 'Pix',
  boleto_parcelado: 'Boleto parcelado',
  boleto_subscription: 'Boleto (recorrência)',
  credit_card_subscription: 'Cartão (recorrência)',
};

const statusLabel: Record<string, string> = {
  [PaymentStatus.PAID]: 'Pago',
  [PaymentStatus.WAITING_PAYMENT]: 'Aguardando pagamento',
  [PaymentStatus.WAITING_REPASS]: 'Aguardando repasse',
  [PaymentStatus.NOTPAID]: 'Não pago',
  [PaymentStatus.CANCELED]: 'Cancelado',
  [PaymentStatus.REFUNDED]: 'Reembolsado',
  [PaymentStatus.ERROR]: 'Erro',
  [PaymentStatus.FREE]: 'Grátis',
};

const statusTone = (status: string): BadgeTone => {
  if (status === PaymentStatus.PAID || status === PaymentStatus.FREE) return 'positive';
  if (status === PaymentStatus.WAITING_PAYMENT || status === PaymentStatus.WAITING_REPASS)
    return 'warning';
  if (status === PaymentStatus.CANCELED || status === PaymentStatus.REFUNDED)
    return 'neutral';
  return 'negative';
};

const AdminSales = () => {
  const location = useLocation();
  const { push } = useHistory();
  const courseid = new URLSearchParams(location.search).get('courseid') ?? '';

  const { fetchCourseInfo } = useAdminApi();

  const { data, isLoading, loadError, currentPage, maxPage, hasMore, reload, loadMore } =
    useAdminResource<CourseInfos, { pages: number; payments: CourseInfos[] }>({
      fetchPage: (page) => fetchCourseInfo(page, courseid),
      select: (response) => response.payments ?? [],
      selectPages: (response) => response.pages ?? 0,
      errorMessage: 'Não foi possível carregar as vendas deste curso.',
    });

  const stats = useMemo(() => {
    const paid = data.filter((item) => item.status_payment === PaymentStatus.PAID);
    const awaiting = data.filter(
      (item) => item.status_payment === PaymentStatus.WAITING_PAYMENT,
    );
    const unpaid = data.filter((item) => item.status_payment === PaymentStatus.NOTPAID);
    const revenue = paid.reduce(
      (sum, item) => sum + (item.payment_info?.amount ?? 0) / 100,
      0,
    );
    return { total: data.length, paid: paid.length, awaiting: awaiting.length, unpaid: unpaid.length, revenue };
  }, [data]);

  const columns: DataTableColumn<CourseInfos>[] = [
    {
      key: 'name',
      header: 'Aluno',
      align: 'start',
      render: (item) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <strong>{item.user_info.fullname}</strong>
          <span style={{ fontSize: 12, opacity: 0.7 }}>{item.user_info.email}</span>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Telefone',
      render: (item) =>
        item.user_info.cellphone
          ? masker(item.user_info.cellphone, ['(99) 9 9999-9999'])
          : '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusBadge
          label={statusLabel[item.status_payment] ?? item.status_payment}
          tone={statusTone(item.status_payment)}
        />
      ),
    },
    {
      key: 'method',
      header: 'Método',
      render: (item) =>
        item.payment_info?.method
          ? methodLabel[item.payment_info.method as PaymentMethod] ?? item.payment_info.method
          : '—',
    },
    {
      key: 'date',
      header: 'Data',
      render: (item) => item.payment_info?.date ?? '—',
    },
    {
      key: 'amount',
      header: 'Valor',
      render: (item) =>
        item.payment_info?.amount
          ? convertNumberToBRL(item.payment_info.amount / 100)
          : '—',
    },
    {
      key: 'installments',
      header: 'Parcelas',
      render: (item) => item.payment_info?.installments ?? '—',
    },
  ];

  if (!courseid) {
    return <CoursePicker />;
  }

  return (
    <>
      <PageHeader
        title="Vendas do curso"
        subtitle={courseid}
        actions={
          <Button onClick={() => push('/admin/courses')}>
            <FiArrowLeft />
            Voltar
          </Button>
        }
      />

      {isLoading && data.length === 0 ? (
        <SkeletonTable rows={4} columns={5} />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={reload} />
      ) : (
        <>
          <StatsGrid>
            <StatCard>
              <StatLabel>Vendas</StatLabel>
              <StatValue>{stats.total}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Pagas</StatLabel>
              <StatValue>{stats.paid}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Aguardando</StatLabel>
              <StatValue>{stats.awaiting}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Não pagas</StatLabel>
              <StatValue>{stats.unpaid}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Receita (pagos)</StatLabel>
              <StatValue>{convertNumberToBRL(stats.revenue)}</StatValue>
            </StatCard>
          </StatsGrid>

          <ListContainer>
            {data.length === 0 ? (
              <EmptyState
                title="Nenhuma venda ainda"
                description="Quando alguém comprar este curso, a venda aparece aqui."
              />
            ) : (
              <DataTable
                columns={columns}
                rows={data}
                rowKey={(item) => item.cartid}
                emptyMessage="Nenhuma venda encontrada."
              />
            )}
          </ListContainer>

          {hasMore && (
            <Button onClick={loadMore} loading={isLoading}>
              Carregar mais…
            </Button>
          )}
          {data.length > 0 && (
            <p style={{ opacity: 0.6, fontSize: 12 }}>
              Página {currentPage + 1} de {Math.max(maxPage, 1)}
            </p>
          )}
        </>
      )}
    </>
  );
};

export default AdminSales;
